/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { ActivityReq } from './activity-req'
import { Activity } from './activity'
import { LoginReq } from './login-req'
import { ActivityUser } from './activity-user'
import { Task } from './task'
import { TaskReflection } from './task-reflection'
import { BatchUpdateWithIdReq } from './batch-update-with-id-req'
import { InformalReflection } from './informal-reflection'
import { UploadFile } from './upload-file'
import { QueryActivitiesQuery } from './query-activities-query'
import { LoadActivityQuery } from './load-activity-query'
import { LoadActivityInfoQuery } from './load-activity-info-query'
import { QueryTasksQuery } from './query-tasks-query'
import { LoadTaskQuery } from './load-task-query'
import { QueryTaskReflectionsQuery } from './query-task-reflections-query'
import { QueryPublishedActivitiesQuery } from './query-published-activities-query'
import { QueryPublishedActivityTasksQuery } from './query-published-activity-tasks-query'
import { QueryActivityReflectionsQuery } from './query-activity-reflections-query'

const buildConfig = createConfigBuilder('growp')

export const activityApi = {
  createActivityConfig(
    activityReq: ActivityReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/activity/activities',
      {},
      null,
      activityReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          activityReflections: (v: any) => v || [],
          reflectionLimit: (v: any) => v || 0,
          inviteCode: (v: any) => v || null,
          logo: (v: any) => v || null,
          bgImage: (v: any) => v || null,
          themeColor: (v: any) => v || null,
          adminIds: (v: any) => v || [],
          description: (v: any) => v || '',
          startedAt: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】创建活动
   * @param activityReq
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  createActivity(
    activityReq: ActivityReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Activity> {
    return axios(activityApi.createActivityConfig(activityReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Activity(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCreateActivity(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ActivityReq) => activityApi.createActivity(payload),
      mutationOptions
    )
  },

  queryActivitiesConfig(
    queryActivitiesQuery?: QueryActivitiesQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities',
      {},
      queryActivitiesQuery
    )
  },

  /**
   * @summary 【后台】查询活动列表
   * @param [queryActivitiesQuery]
   * @param [queryActivitiesQuery.nameLike]
   * @param [queryActivitiesQuery.expand]
   * @param [queryActivitiesQuery.paginated] 是否分页，默认 1
   * @param [queryActivitiesQuery.page] 显示的数据页，默认 1
   * @param [queryActivitiesQuery.perPage] 每页显示数量，默认 20
   * @param [queryActivitiesQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Activity[]>
   */
  queryActivities<TQuery extends QueryActivitiesQuery>(
    queryActivitiesQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Activity, TQuery['expand']>[]> {
    return axios(
      activityApi.queryActivitiesConfig(queryActivitiesQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Activity(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryActivities<TQuery extends QueryActivitiesQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Activity, TQuery['expand']>[]> {
    return useQueryApi<Expand<Activity, TQuery['expand']>[]>(
      activityApi.queryActivities,
      'Activity.queryActivities',
      queryOptions,
      query
    )
  },

  updateActivityConfig(
    id: number,
    activityReq: ActivityReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/activity/activities/{id}',
      {
        id,
      },
      null,
      activityReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          activityReflections: (v: any) => v || [],
          reflectionLimit: (v: any) => v || 0,
          inviteCode: (v: any) => v || null,
          logo: (v: any) => v || null,
          bgImage: (v: any) => v || null,
          themeColor: (v: any) => v || null,
          adminIds: (v: any) => v || [],
          description: (v: any) => v || '',
          startedAt: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【后台】编辑活动
   * @param id
   * @param activityReq
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  updateActivity(
    id: number,
    activityReq: ActivityReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Activity> {
    return axios(
      activityApi.updateActivityConfig(id, activityReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Activity(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateActivity(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: ActivityReq }) =>
        activityApi.updateActivity(id, payload),
      mutationOptions
    )
  },

  loadActivityConfig(
    id: number,
    loadActivityQuery?: LoadActivityQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}',
      {
        id,
      },
      loadActivityQuery
    )
  },

  /**
   * @summary 【后台】查询活动
   * @param id
   * @param [loadActivityQuery]
   * @param [loadActivityQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  loadActivity<TQuery extends LoadActivityQuery>(
    id: number,
    loadActivityQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Activity, TQuery['expand']>> {
    return axios(
      activityApi.loadActivityConfig(id, loadActivityQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Activity(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadActivity<TQuery extends LoadActivityQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Activity, TQuery['expand']>> {
    return useQueryApi<Expand<Activity, TQuery['expand']>>(
      activityApi.loadActivity,
      'Activity.loadActivity',
      queryOptions,
      id,
      query
    )
  },

  deleteActivityConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'delete', '/activity/activities/{id}', {
      id,
    })
  },

  /**
   * @summary 【后台】删除活动
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteActivity(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(activityApi.deleteActivityConfig(id, options)) as any
  },

  useDeleteActivity(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (id: number) => activityApi.deleteActivity(id),
      mutationOptions
    )
  },

  copyActivityConfig(
    name: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'post', '/activity/activities/{id}/copy', {
      name,
      id,
    })
  },

  /**
   * @summary 【后台】复制活动
   *  - 非正式用户参与的活动，复制：活动+活动任务
   *  - 正式用户参与的活动，复制：活动+活动任务+活动人员
   * @param name 复制任务的新名字
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  copyActivity(
    name: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Activity> {
    return axios(activityApi.copyActivityConfig(name, id, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new Activity(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useCopyActivity(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ name, id }: { name: string; id: number }) =>
        activityApi.copyActivity(name, id),
      mutationOptions
    )
  },

  loadActivityInfoConfig(
    id: number,
    loadActivityInfoQuery?: LoadActivityInfoQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}/info',
      {
        id,
      },
      loadActivityInfoQuery
    )
  },

  /**
   * @summary 【前台】非正式用户/正式用户-登录活动页获取活动信息
   * @param id
   * @param [loadActivityInfoQuery]
   * @param [loadActivityInfoQuery.expand] 仅允许 expand
   *  - reflection_count
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  loadActivityInfo<TQuery extends LoadActivityInfoQuery>(
    id: number,
    loadActivityInfoQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Activity, TQuery['expand']>> {
    return axios(
      activityApi.loadActivityInfoConfig(id, loadActivityInfoQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Activity(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadActivityInfo<TQuery extends LoadActivityInfoQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Activity, TQuery['expand']>> {
    return useQueryApi<Expand<Activity, TQuery['expand']>>(
      activityApi.loadActivityInfo,
      'Activity.loadActivityInfo',
      queryOptions,
      id,
      query
    )
  },

  loginActivityConfig(
    id: number,
    loginReq: LoginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/activity/activities/{id}/login',
      {
        id,
      },
      null,
      loginReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => (v === 0 ? v : v || null),
          account: (v: any) => v || '',
          password: (v: any) => v || '',
          phone: (v: any) => v || '',
          reminderId: (v: any) => v || '',
          multiRid: (v: any) => v || 0,
          openLogin: (v: any) => v || null,
        },
      }
    )
  },

  /**
   * @summary 【前台】非正式用户-登录活动
   * @param id
   * @param loginReq
   * @param [options]
   * @return AxiosResponsePromise<ActivityUser>
   */
  loginActivity(
    id: number,
    loginReq: LoginReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ActivityUser> {
    return axios(activityApi.loginActivityConfig(id, loginReq, options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? new ActivityUser(_data) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useLoginActivity(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: LoginReq }) =>
        activityApi.loginActivity(id, payload),
      mutationOptions
    )
  },

  updateActivityLoginTypeConfig(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/activity/activities/{id}/login-type/{type}',
      {
        type,
        id,
      }
    )
  },

  /**
   * @summary 【后台】编辑活动-登录类型
   * @param type 活动登录状态
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<Activity>
   */
  updateActivityLoginType(
    type: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Activity> {
    return axios(
      activityApi.updateActivityLoginTypeConfig(type, id, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Activity(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateActivityLoginType(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ type, id }: { type: string; id: number }) =>
        activityApi.updateActivityLoginType(type, id),
      mutationOptions
    )
  },

  queryTasksConfig(
    id: number,
    queryTasksQuery?: QueryTasksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}/tasks',
      {
        id,
      },
      queryTasksQuery
    )
  },

  /**
   * @summary 【后台】查询活动任务列表
   * @param id
   * @param [queryTasksQuery]
   * @param [queryTasksQuery.expand]
   * @param [queryTasksQuery.paginated] 是否分页，默认 1
   * @param [queryTasksQuery.page] 显示的数据页，默认 1
   * @param [queryTasksQuery.perPage] 每页显示数量，默认 20
   * @param [queryTasksQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Task[]>
   */
  queryTasks<TQuery extends QueryTasksQuery>(
    id: number,
    queryTasksQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Task, TQuery['expand']>[]> {
    return axios(
      activityApi.queryTasksConfig(id, queryTasksQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Task(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryTasks<TQuery extends QueryTasksQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Task, TQuery['expand']>[]> {
    return useQueryApi<Expand<Task, TQuery['expand']>[]>(
      activityApi.queryTasks,
      'Activity.queryTasks',
      queryOptions,
      id,
      query
    )
  },

  deleteTaskConfig(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/activity/activities/{id}/tasks/{taskId}',
      {
        taskId,
        id,
      }
    )
  },

  /**
   * @summary 【后台】删除活动
   *   - 课程
   *   - 测验
   * @param taskId
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  deleteTask(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(activityApi.deleteTaskConfig(taskId, id, options)) as any
  },

  useDeleteTask(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ taskId, id }: { taskId: number; id: number }) =>
        activityApi.deleteTask(taskId, id),
      mutationOptions
    )
  },

  loadTaskConfig(
    taskId: number,
    id: number,
    loadTaskQuery?: LoadTaskQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}/tasks/{taskId}',
      {
        taskId,
        id,
      },
      loadTaskQuery
    )
  },

  /**
   * @summary 【后台】查询单个活动
   *  - 仅返回活动信息，不含其它活动业务信息
   * @param taskId
   * @param id
   * @param [loadTaskQuery]
   * @param [loadTaskQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Task>
   */
  loadTask<TQuery extends LoadTaskQuery>(
    taskId: number,
    id: number,
    loadTaskQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Task, TQuery['expand']>> {
    return axios(
      activityApi.loadTaskConfig(taskId, id, loadTaskQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Task(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadTask<TQuery extends LoadTaskQuery>(
    {
      taskId,
      id,
      query,
    }: {
      taskId: number
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Task, TQuery['expand']>> {
    return useQueryApi<Expand<Task, TQuery['expand']>>(
      activityApi.loadTask,
      'Activity.loadTask',
      queryOptions,
      taskId,
      id,
      query
    )
  },

  loadTaskCheckConfig(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}/tasks/{taskId}/check',
      {
        taskId,
        id,
      }
    )
  },

  /**
   * @summary 【前台】查询单个活动
   *  - 查询活动详情预检查
   * @param taskId
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  loadTaskCheck(
    taskId: number,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(activityApi.loadTaskCheckConfig(taskId, id, options)) as any
  },

  useLoadTaskCheck(
    {
      taskId,
      id,
    }: {
      taskId: number
      id: number
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<boolean> {
    return useQueryApi<boolean>(
      activityApi.loadTaskCheck,
      'Activity.loadTaskCheck',
      queryOptions,
      taskId,
      id
    )
  },

  queryTaskReflectionsConfig(
    taskId: number,
    id: number,
    queryTaskReflectionsQuery?: QueryTaskReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/activities/{id}/tasks/{taskId}/reflections',
      {
        taskId,
        id,
      },
      queryTaskReflectionsQuery
    )
  },

  /**
   * @summary 【后台】查询活动任务列表
   * @param taskId
   * @param id
   * @param [queryTaskReflectionsQuery]
   * @param [queryTaskReflectionsQuery.reflection.nameOrPhoneLikes] 根据名字或手机号筛选，多个用英文逗号分隔
   * @param [queryTaskReflectionsQuery.reflection.nameIn]
   * @param [queryTaskReflectionsQuery.reflection.nameLike]
   * @param [queryTaskReflectionsQuery.reflection.accountLike]
   * @param [queryTaskReflectionsQuery.reflection.accountIn]
   * @param [queryTaskReflectionsQuery.reflection.genderIn]
   * @param [queryTaskReflectionsQuery.reflection.statusIn]
   * @param [queryTaskReflectionsQuery.reflection.phoneIn]
   * @param [queryTaskReflectionsQuery.reflection.phoneLike]
   * @param [queryTaskReflectionsQuery.reflection.emailIn]
   * @param [queryTaskReflectionsQuery.reflection.emailLike]
   * @param [queryTaskReflectionsQuery.expand]
   * @param [queryTaskReflectionsQuery.endAtEgt] 任务-完成时间-大于
   * @param [queryTaskReflectionsQuery.endAtElt] 任务-完成时间-小于
   * @param [queryTaskReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryTaskReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryTaskReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [queryTaskReflectionsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<TaskReflection[]>
   */
  queryTaskReflections<TQuery extends QueryTaskReflectionsQuery>(
    taskId: number,
    id: number,
    queryTaskReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<TaskReflection, TQuery['expand']>[]> {
    return axios(
      activityApi.queryTaskReflectionsConfig(
        taskId,
        id,
        queryTaskReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new TaskReflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryTaskReflections<TQuery extends QueryTaskReflectionsQuery>(
    {
      taskId,
      id,
      query,
    }: {
      taskId: number
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<TaskReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<TaskReflection, TQuery['expand']>[]>(
      activityApi.queryTaskReflections,
      'Activity.queryTaskReflections',
      queryOptions,
      taskId,
      id,
      query
    )
  },

  batchDeleteTaskReflectionsConfig(
    taskId: number,
    id: number,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/activity/activities/{id}/tasks/{taskId}/reflections/$batch',
      {
        taskId,
        id,
      },
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】删除活动人员
   * @param taskId
   * @param id
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDeleteTaskReflections(
    taskId: number,
    id: number,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      activityApi.batchDeleteTaskReflectionsConfig(
        taskId,
        id,
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchDeleteTaskReflections(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: BatchUpdateWithIdReq
      }) => activityApi.batchDeleteTaskReflections(taskId, id, payload),
      mutationOptions
    )
  },

  batchAddTaskReflectionsConfig(
    taskId: number,
    id: number,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/activity/activities/{id}/tasks/{taskId}/reflections/$batch',
      {
        taskId,
        id,
      },
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】从活动人员里选则人员添加至某活动任务内
   * @param taskId
   * @param id
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchAddTaskReflections(
    taskId: number,
    id: number,
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      activityApi.batchAddTaskReflectionsConfig(
        taskId,
        id,
        batchUpdateWithIdReq,
        options
      )
    ) as any
  },

  useBatchAddTaskReflections(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        taskId,
        id,
        payload,
      }: {
        taskId: number
        id: number
        payload: BatchUpdateWithIdReq
      }) => activityApi.batchAddTaskReflections(taskId, id, payload),
      mutationOptions
    )
  },

  queryPublishedActivitiesConfig(
    queryPublishedActivitiesQuery?: QueryPublishedActivitiesQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/published-activities',
      {},
      queryPublishedActivitiesQuery
    )
  },

  /**
   * @summary 【前台】查询活动列表
   * @param [queryPublishedActivitiesQuery]
   * @param [queryPublishedActivitiesQuery.paginated] 是否分页，默认 1
   * @param [queryPublishedActivitiesQuery.page] 显示的数据页，默认 1
   * @param [queryPublishedActivitiesQuery.perPage] 每页显示数量，默认 20
   * @param [queryPublishedActivitiesQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Activity[]>
   */
  queryPublishedActivities(
    queryPublishedActivitiesQuery?: QueryPublishedActivitiesQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Activity[]> {
    return axios(
      activityApi.queryPublishedActivitiesConfig(
        queryPublishedActivitiesQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Activity(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryPublishedActivities(
    query?: QueryPublishedActivitiesQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Activity[]> {
    return useQueryApi<Activity[]>(
      activityApi.queryPublishedActivities,
      'Activity.queryPublishedActivities',
      queryOptions,
      query
    )
  },

  queryPublishedActivityTasksConfig(
    id: number,
    queryPublishedActivityTasksQuery?: QueryPublishedActivityTasksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/published-activities/{id}/tasks',
      {
        id,
      },
      queryPublishedActivityTasksQuery
    )
  },

  /**
   * @summary 【前台】查询活动任务列表
   *  - 过滤 摇号、面试
   * @param id
   * @param [queryPublishedActivityTasksQuery]
   * @param [queryPublishedActivityTasksQuery.paginated] 是否分页，默认 1
   * @param [queryPublishedActivityTasksQuery.page] 显示的数据页，默认 1
   * @param [queryPublishedActivityTasksQuery.perPage] 每页显示数量，默认 20
   * @param [queryPublishedActivityTasksQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Task[]>
   */
  queryPublishedActivityTasks(
    id: number,
    queryPublishedActivityTasksQuery?: QueryPublishedActivityTasksQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Task[]> {
    return axios(
      activityApi.queryPublishedActivityTasksConfig(
        id,
        queryPublishedActivityTasksQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Task(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryPublishedActivityTasks(
    {
      id,
      query,
    }: {
      id: number
      query?: QueryPublishedActivityTasksQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Task[]> {
    return useQueryApi<Task[]>(
      activityApi.queryPublishedActivityTasks,
      'Activity.queryPublishedActivityTasks',
      queryOptions,
      id,
      query
    )
  },

  queryActivityReflectionsConfig(
    id: number,
    queryActivityReflectionsQuery?: QueryActivityReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/{id}/reflections',
      {
        id,
      },
      queryActivityReflectionsQuery
    )
  },

  /**
   * @summary 【后台】查询活动用户列表
   * @param id
   * @param [queryActivityReflectionsQuery]
   * @param [queryActivityReflectionsQuery.expand]
   * @param [queryActivityReflectionsQuery.paginated] 是否分页，默认 1
   * @param [queryActivityReflectionsQuery.page] 显示的数据页，默认 1
   * @param [queryActivityReflectionsQuery.perPage] 每页显示数量，默认 20
   * @param [queryActivityReflectionsQuery.nameIn]
   * @param [queryActivityReflectionsQuery.nameLike] 名字模糊查询
   * @param [queryActivityReflectionsQuery.accountIn] 多个账户查询
   * @param [queryActivityReflectionsQuery.accountLike] 账户模糊查询
   * @param [queryActivityReflectionsQuery.phone]
   * @param [queryActivityReflectionsQuery.phoneLike] 手机号模糊查询
   * @param [queryActivityReflectionsQuery.phoneIn]
   * @param [queryActivityReflectionsQuery.usinIn] 学工号查询
   * @param [queryActivityReflectionsQuery.usinLike] 学工号模糊查询
   * @param [queryActivityReflectionsQuery.createdAtEgt] 创建时间大于
   * @param [queryActivityReflectionsQuery.createdAtElt] 创建时间小于
   * @param [queryActivityReflectionsQuery.statusIn]
   * @param [queryActivityReflectionsQuery.roleIn]
   * @param [queryActivityReflectionsQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<InformalReflection[]>
   */
  queryActivityReflections<TQuery extends QueryActivityReflectionsQuery>(
    id: number,
    queryActivityReflectionsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<InformalReflection, TQuery['expand']>[]> {
    return axios(
      activityApi.queryActivityReflectionsConfig(
        id,
        queryActivityReflectionsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data
        ? _data.map((d: any) => new InformalReflection(d))
        : _data
      return { data, ...rest }
    }) as any
  },

  useQueryActivityReflections<TQuery extends QueryActivityReflectionsQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<InformalReflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<InformalReflection, TQuery['expand']>[]>(
      activityApi.queryActivityReflections,
      'Activity.queryActivityReflections',
      queryOptions,
      id,
      query
    )
  },

  downloadImportReflectionTemplateConfig(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/activity/{id}/reflections/download-template',
      {
        id,
      }
    )
  },

  /**
   * @summary 【后台】下载导入用户模板接口
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  downloadImportReflectionTemplate(
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      activityApi.downloadImportReflectionTemplateConfig(id, options)
    ) as any
  },

  useDownloadImportReflectionTemplate(
    id: number,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      activityApi.downloadImportReflectionTemplate,
      'Activity.downloadImportReflectionTemplate',
      queryOptions,
      id
    )
  },

  queryExportedReflectionConfig(
    keys: string,
    idIn: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/activity/{id}/reflections/exported', {
      keys,
      idIn,
      id,
    })
  },

  /**
   * @summary 活动人员列表导出
   * @param keys
   * @param idIn
   * @param id
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedReflection(
    keys: string,
    idIn: string,
    id: number,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      activityApi.queryExportedReflectionConfig(keys, idIn, id, options)
    ) as any
  },

  useQueryExportedReflection(
    {
      keys,
      idIn,
      id,
    }: {
      keys: string
      idIn: string
      id: number
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      activityApi.queryExportedReflection,
      'Activity.queryExportedReflection',
      queryOptions,
      keys,
      idIn,
      id
    )
  },

  importReflectionConfig(
    id: number,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/activity/{id}/reflections/import',
      {
        id,
      },
      null,
      uploadFile,
      {
        format: 'form',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】导入活动用户
   * @param id
   * @param uploadFile
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  importReflection(
    id: number,
    uploadFile: UploadFile,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      activityApi.importReflectionConfig(id, uploadFile, options)
    ) as any
  },

  useImportReflection(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: UploadFile }) =>
        activityApi.importReflection(id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(activityApi).forEach((api) => {
  // @ts-ignore
  activityApi[api]._name_ = `Activity.${api}`
})
